// === INPACT:SLUITSTRAAT:BOILERPLATE:SETTINGS-PAGE:v1 ===

/**
 * SettingsPage - Configuration Dashboard
 *
 * PURPOSE:
 * This dashboard view is for configuration and settings management.
 * It shows configuration status, modules, and settings overview.
 * Accessible at /settings for reconfiguration at any time.
 *
 * USAGE:
 * - Development: Accessible for initial setup and testing
 * - Production: Can be accessed by administrators to reconfigure the instance
 *
 * DESIGN REVIEW NOTES:
 *
 * What Works Well:
 * - Dashboard structure: Clear separation of Customer Info, Modules Overview, Quick Links, Getting Started
 * - Active/Inactive Modules: Split with cards and greyed-out inactive ones makes it obvious what's running
 * - Checklist with progress bar: Adds interactivity and sense of progress
 * - Quick Links section: Easy navigation to important actions
 * - Environment & instance badge: Nice touch in header
 *
 * Possible Improvements:
 * - Visual hierarchy: Give Modules Overview more prominence as heart of sluitstraat
 * - Module cards: Add icons consistently, tooltips/expandable descriptions, click-through to config
 * - Customer branding: Show customer logo, use their color scheme in accents
 * - Checklist: Auto-detection when config updated, links next to items for actions
 * - Footer: Add last updated date, environment badges (Dev/Test/Prod) with colors
 *
 * Future Nice-to-Haves:
 * - Notifications panel: Warnings (e.g., "ProductService inactive but required by workflow")
 * - Search bar: Quick search across modules/configs
 * - Responsive design: Cards collapse neatly on tablet/mobile
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useConfig,
  ModuleDetailsModal,
  getSetupCompletionStatus,
  LanguageSelector,
} from '@sluitstraat/core';

interface ModuleInfo {
  id: string;
  name: string;
  description: string;
  icon: string;
  enabled: boolean;
  category?: string;
  dependencies?: string[];
  detailedDescription?: string;
}

interface ChecklistStep {
  id: string;
  icon: string;
  title: string;
  description: string;
  completed: boolean;
  route: string;
  disabled?: boolean;
  disabledReason?: string;
}

const SettingsPage: React.FC = () => {
  const { config } = useConfig();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [_settingsProgress, setSettingsProgress] = useState(0);

  // Modal states (kept for potential future use)
  const [selectedModule] = useState<ModuleInfo | null>(null);
  const [showModuleModal, setShowModuleModal] = useState(false);

  // Get modules from config with enhanced metadata
  const modules: ModuleInfo[] = React.useMemo(() => {
    if (!config.modules) return [];

    const moduleMap: Record<string, {
      name: string;
      description: string;
      detailedDescription: string;
      icon: string;
      category: 'Core' | 'Optional';
      dependencies?: string[];
    }> = {
      calculatorbox: {
        name: 'Calculator Box',
        description: 'Premium calculation engine',
        detailedDescription: 'Advanced calculation engine for insurance premium calculations, supporting multiple product types, risk assessments, and discount rules.',
        icon: '🧮',
        category: 'Optional',
        dependencies: []
      },
      clientservice: {
        name: 'Client Service',
        description: 'Customer management system',
        detailedDescription: 'Comprehensive customer data management including profiles, policies, claims history, and communication tracking.',
        icon: '👥',
        category: 'Optional',
        dependencies: []
      },
      jsonforms: {
        name: 'JSON Forms',
        description: 'Dynamic form builder (Core Module)',
        detailedDescription: 'Flexible form generation system based on JSON schemas with built-in validation, conditional fields, and custom renderers. This is a core module and cannot be disabled.',
        icon: '📝',
        category: 'Core',
        dependencies: []
      },
      productservice: {
        name: 'Product Service',
        description: 'Product catalog management',
        detailedDescription: 'Complete product lifecycle management including blueprints, versions, configurations, and multi-carrier support.',
        icon: '📦',
        category: 'Optional',
        dependencies: []
      },
      quotation: {
        name: 'Quotation',
        description: 'Quote generation system',
        detailedDescription: 'Generate, manage, and track insurance quotes with automated calculations, PDF exports, and customer delivery.',
        icon: '💼',
        category: 'Optional',
        dependencies: ['calculatorbox', 'productservice']
      },
      customer_portal: {
        name: 'Customer Portal',
        description: 'Self-service portal',
        detailedDescription: 'Customer-facing portal for policy management, claims submission, document access, and account settings.',
        icon: '🏠',
        category: 'Optional',
        dependencies: ['clientservice']
      },
    };

    return Object.entries(config.modules).map(([id, moduleConfig]) => ({
      id,
      name: moduleMap[id]?.name || id,
      description: moduleMap[id]?.description || 'Module description',
      detailedDescription: moduleMap[id]?.detailedDescription || 'No detailed description available.',
      icon: moduleMap[id]?.icon || '⚙️',
      category: moduleMap[id]?.category || 'Optional',
      dependencies: moduleMap[id]?.dependencies || [],
      enabled: moduleConfig?.enabled || false,
    }));
  }, [config.modules]);

  // Calculate setup progress
  useEffect(() => {
    const completionStatus = getSetupCompletionStatus(config);
    const steps = Object.values(completionStatus);
    const completed = steps.filter(Boolean).length;
    setSettingsProgress((completed / steps.length) * 100);
  }, [config, modules]);

  // Reload config when returning to this page (to pick up changes from setup pages)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible - reload config in case it was updated
        window.location.reload();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const handleToggleModule = (moduleId: string) => {
    // This would update config.json in a real implementation
    alert(`Module activation toggle would update config.json\nModule: ${moduleId}`);
  };

  // Enhanced checklist with icons, routes, and completion status
  const checklistSteps: ChecklistStep[] = React.useMemo(() => {
    const completionStatus = getSetupCompletionStatus(config);
    const hasActiveModules = modules.some(m => m.enabled);

    return [
      {
        id: 'customer',
        icon: '🏢',
        title: t('settings.dashboard.steps.customer.title'),
        description: t('settings.dashboard.steps.customer.description'),
        completed: completionStatus.customerInfo,
        route: '/settings/customer',
      },
      {
        id: 'sluitstraat',
        icon: '⚙️',
        title: config.branding?.platformName || 'Sluitstraat',
        description: t('settings.dashboard.steps.sluitstraat.description'),
        completed: completionStatus.sluitstraatInfo,
        route: '/settings/sluitstraat',
      },
      {
        id: 'modules',
        icon: '📦',
        title: t('settings.dashboard.steps.modules.title'),
        description: t('settings.dashboard.steps.modules.description'),
        completed: completionStatus.modules,
        route: '/settings/module',
      },
      {
        id: 'workflow',
        icon: '🔄',
        title: t('settings.dashboard.steps.workflow.title'),
        description: t('settings.dashboard.steps.workflow.description'),
        completed: completionStatus.workflow,
        disabled: !hasActiveModules,
        disabledReason: t('settings.dashboard.steps.workflow.disabledReason'),
        route: '/settings/workflow',
      },
    ];
  }, [config, modules, t]);

  const activeModules = modules.filter(m => m.enabled);

  return (
    <div style={styles.container}>
      {/* Header with branding */}
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            {config.customer?.logo ? (
              <img
                src={config.customer.logo}
                alt={config.texts?.general?.applicationName || config.branding?.appName || 'Logo'}
                style={styles.logo}
              />
            ) : (
              <div style={styles.logoPlaceholder}>
                {(config.texts?.general?.applicationName || config.branding?.appName || 'I').charAt(0)}
              </div>
            )}
            <div>
              <h1 style={styles.headerTitle}>{config.texts?.general?.applicationName || config.branding.appName}</h1>
              <p style={styles.headerSubtitle}>
                {config.texts?.general?.tagline || config.customer?.tagline || `${config.branding?.platformName || 'Sluitstraat'} Portal`}
              </p>
            </div>
          </div>
          <div style={styles.headerRight}>
            <LanguageSelector />
          </div>
        </div>
      </header>

      {/* Main Dashboard Content */}
      <main style={styles.main}>
        {/* Notifications Panel */}
        {modules.some(m => !m.enabled && config.workflow?.screens?.some((s: any) => s.requiresModules?.includes(m.id))) && (
          <div style={styles.notificationBanner}>
            <span style={styles.notificationIcon}>⚠️</span>
            <div>
              <strong>Warning:</strong> Some workflow screens require modules that are currently inactive.
              Check your module configuration.
            </div>
          </div>
        )}

        <div style={styles.dashboard}>
          {/* Left Column - Navigation & Info */}
          <div style={styles.leftColumn}>
            {/* System Information Card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>{t('settings.dashboard.systemInfo.title')}</h2>
              </div>
              <div style={styles.cardBody}>
                <div style={styles.systemInfoRow}>
                  <span style={styles.systemInfoLabel}>{t('settings.dashboard.systemInfo.instanceId')}:</span>
                  <span style={styles.systemInfoValue}>
                    {config.customer?.id || 'boilerplate'}
                  </span>
                </div>
                <div style={styles.systemInfoRow}>
                  <span style={styles.systemInfoLabel}>{t('settings.dashboard.systemInfo.environment')}:</span>
                  <div style={{
                    ...styles.environmentBadgeSmall,
                    backgroundColor: config.environment === 'production' ? '#10b981' :
                                    config.environment === 'staging' ? '#f59e0b' : '#6b7280'
                  }}>
                    {config.environment?.toUpperCase() || 'DEVELOPMENT'}
                  </div>
                </div>
                <div style={styles.systemInfoRow}>
                  <span style={styles.systemInfoLabel}>{t('settings.dashboard.systemInfo.version')}:</span>
                  <span style={styles.systemInfoValue}>{config.version || '1.0'}</span>
                </div>
                <div style={styles.systemInfoRow}>
                  <span style={styles.systemInfoLabel}>{t('settings.dashboard.systemInfo.lastUpdated')}:</span>
                  <span style={styles.systemInfoValue}>
                    {new Date().toLocaleDateString()} {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            </div>

            {/* Customer Info Card */}
            <div style={{
              ...styles.card,
              borderTop: config.branding?.primaryColor ? `4px solid ${config.branding.primaryColor}` : undefined,
            }}>
              <div style={{
                ...styles.cardHeader,
                backgroundColor: config.branding?.primaryColor ? `${config.branding.primaryColor}15` : undefined,
              }}>
                <h2 style={{
                  ...styles.cardTitle,
                  color: config.branding?.primaryColor || styles.cardTitle.color,
                }}>{t('settings.dashboard.sluitstraatInfo.title')}</h2>
              </div>
              <div style={styles.cardBody}>
                {/* Logo */}
                <div style={styles.customerLogoSection}>
                  {config.customer?.logo ? (
                    <img
                      src={config.customer.logo}
                      alt={config.texts?.general?.applicationName || config.branding?.appName || 'Customer logo'}
                      style={{
                        ...styles.customerLogo,
                        borderColor: config.branding?.primaryColor || '#e5e7eb',
                      }}
                    />
                  ) : (
                    <div style={{
                      ...styles.customerLogoPlaceholder,
                      borderColor: config.branding?.primaryColor || '#d1d5db',
                      color: config.branding?.primaryColor || '#9ca3af',
                    }}>
                      {(config.texts?.general?.applicationName || config.branding?.appName || 'N/A').charAt(0)}
                    </div>
                  )}
                </div>

                {/* Customer Details */}
                <div style={styles.customerInfoSection}>
                  <div style={styles.customerNameBlock}>
                    <h3 style={{
                      ...styles.customerName,
                      color: config.branding?.textColor || styles.customerName.color,
                    }}>
                      {config.texts?.general?.applicationName || config.branding?.appName || config.customer?.name || <span style={styles.notSet}>{t('settings.dashboard.notSet')}</span>}
                    </h3>
                    {(config.texts?.general?.tagline || config.customer?.tagline) && (
                      <p style={styles.customerTagline}>
                        {config.texts?.general?.tagline || config.customer?.tagline}
                      </p>
                    )}
                  </div>
                </div>

                {/* Contact Information - Expanded */}
                <div style={styles.contactSection}>
                  <h3 style={styles.contactSectionTitle}>{t('settings.dashboard.sluitstraatInfo.contact')}</h3>

                  {(config.texts?.contact?.email || config.customer?.contact?.email) && (
                    <div style={styles.contactRow}>
                      <span style={styles.contactIcon}>📧</span>
                      <div style={styles.contactInfo}>
                        <span style={styles.contactLabel}>{t('settings.dashboard.sluitstraatInfo.email')}</span>
                        <a href={`mailto:${config.texts?.contact?.email || config.customer?.contact?.email}`} style={{
                          ...styles.contactLink,
                          color: config.branding?.primaryColor || 'var(--primary-color, #056666)',
                        }}>
                          {config.texts?.contact?.email || config.customer?.contact?.email}
                        </a>
                      </div>
                    </div>
                  )}

                  {(config.texts?.contact?.phone || config.customer?.contact?.phone?.main) && (
                    <div style={styles.contactRow}>
                      <span style={styles.contactIcon}>📞</span>
                      <div style={styles.contactInfo}>
                        <span style={styles.contactLabel}>{t('settings.dashboard.sluitstraatInfo.phone')}</span>
                        <a href={`tel:${config.texts?.contact?.phone || config.customer?.contact?.phone?.main}`} style={{
                          ...styles.contactLink,
                          color: config.branding?.primaryColor || 'var(--primary-color, #056666)',
                        }}>
                          {config.texts?.contact?.phone || config.customer?.contact?.phone?.main}
                        </a>
                      </div>
                    </div>
                  )}

                  {(config.texts?.contact?.website || config.customer?.contact?.website) && (
                    <div style={styles.contactRow}>
                      <span style={styles.contactIcon}>🌐</span>
                      <div style={styles.contactInfo}>
                        <span style={styles.contactLabel}>{t('settings.dashboard.sluitstraatInfo.website')}</span>
                        <a href={config.texts?.contact?.website || config.customer?.contact?.website} target="_blank" rel="noopener noreferrer" style={{
                          ...styles.contactLink,
                          color: config.branding?.primaryColor || 'var(--primary-color, #056666)',
                        }}>
                          {config.texts?.contact?.website || config.customer?.contact?.website}
                        </a>
                      </div>
                    </div>
                  )}

                  {!(config.texts?.contact?.email || config.customer?.contact?.email) &&
                   !(config.texts?.contact?.phone || config.customer?.contact?.phone?.main) &&
                   !(config.texts?.contact?.website || config.customer?.contact?.website) && (
                    <div style={styles.emptyContactState}>
                      <span style={styles.notSet}>{t('settings.dashboard.sluitstraatInfo.noContact')}</span>
                    </div>
                  )}
                </div>

                {/* Branding Preview */}
                {config.branding && (
                  <div style={styles.brandingPreview}>
                    <h3 style={styles.contactSectionTitle}>{t('settings.dashboard.sluitstraatInfo.brandingPreview')}</h3>
                    <div style={styles.brandingColorGrid}>
                      {config.branding.primaryColor && (
                        <div style={styles.brandingColorItem}>
                          <div style={{
                            ...styles.brandingColorSwatch,
                            backgroundColor: config.branding.primaryColor
                          }} />
                          <span style={styles.brandingColorLabel}>{t('settings.dashboard.sluitstraatInfo.primary')}</span>
                        </div>
                      )}
                      {config.branding.secondaryColor && (
                        <div style={styles.brandingColorItem}>
                          <div style={{
                            ...styles.brandingColorSwatch,
                            backgroundColor: config.branding.secondaryColor
                          }} />
                          <span style={styles.brandingColorLabel}>{t('settings.dashboard.sluitstraatInfo.secondary')}</span>
                        </div>
                      )}
                      {config.branding.accentColor && (
                        <div style={styles.brandingColorItem}>
                          <div style={{
                            ...styles.brandingColorSwatch,
                            backgroundColor: config.branding.accentColor
                          }} />
                          <span style={styles.brandingColorLabel}>{t('settings.dashboard.sluitstraatInfo.accent')}</span>
                        </div>
                      )}
                      {config.branding.backgroundColor && (
                        <div style={styles.brandingColorItem}>
                          <div style={{
                            ...styles.brandingColorSwatch,
                            backgroundColor: config.branding.backgroundColor,
                            border: '2px solid #d1d5db',
                          }} />
                          <span style={styles.brandingColorLabel}>{t('settings.dashboard.sluitstraatInfo.background')}</span>
                        </div>
                      )}
                      {config.branding.textColor && (
                        <div style={styles.brandingColorItem}>
                          <div style={{
                            ...styles.brandingColorSwatch,
                            backgroundColor: config.branding.textColor
                          }} />
                          <span style={styles.brandingColorLabel}>{t('settings.dashboard.sluitstraatInfo.text')}</span>
                        </div>
                      )}
                    </div>
                    {config.branding.fontFamily && (
                      <div style={styles.fontPreview}>
                        <span style={styles.fontPreviewLabel}>{t('settings.dashboard.sluitstraatInfo.font')}:</span>
                        <span style={{
                          ...styles.fontPreviewValue,
                          fontFamily: config.branding.fontFamily,
                        }}>
                          {config.branding.fontFamily.split(',')[0].replace(/['"]/g, '')}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Quick Links Card */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h2 style={styles.cardTitle}>{t('settings.dashboard.quickLinks.title')}</h2>
              </div>
              <div style={styles.cardBody}>
                <button
                  onClick={() => window.open('https://www.inpactsolutions.nl/', '_blank')}
                  style={styles.quickLinkButton}
                >
                  <span style={styles.quickLinkIcon}>🌐</span>
                  <span>{t('settings.dashboard.quickLinks.companyPortal')}</span>
                </button>
                <button
                  onClick={() => window.open('https://www.inpactsolutions.nl/contact/', '_blank')}
                  style={styles.quickLinkButton}
                >
                  <span style={styles.quickLinkIcon}>💬</span>
                  <span>{t('settings.dashboard.quickLinks.support')}</span>
                </button>

              </div>
            </div>
          </div>

          {/* Right Column - Setup & Modules */}
          <div style={styles.rightColumn}>
            {/* Settings Navigation Grid */}
            <div style={styles.settingsGrid}>
              {checklistSteps.map((step) => (
                <div
                  key={step.id}
                  style={{
                    ...styles.settingsGridCard,
                    ...(step.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {})
                  }}
                  onClick={() => !step.disabled && navigate(step.route)}
                  title={step.disabled ? step.disabledReason : `Configure ${step.title}`}
                >
                  <div style={styles.settingsGridIcon}>
                    {step.icon}
                  </div>
                  <h3 style={styles.settingsGridTitle}>{step.title}</h3>
                  <p style={styles.settingsGridDescription}>{step.description}</p>
                  {step.disabled && step.disabledReason && (
                    <span style={styles.settingsGridWarning}>{step.disabledReason}</span>
                  )}
                  {!step.disabled && <span style={styles.settingsGridArrow}>&#8594;</span>}
                </div>
              ))}

              {/* Content & Localization Card */}
              <div
                style={styles.settingsGridCard}
                onClick={() => navigate('/settings/content')}
              >
                <div style={styles.settingsGridIcon}>Aa</div>
                <h3 style={styles.settingsGridTitle}>{t('settings.dashboard.contentLocalization.title')}</h3>
                <p style={styles.settingsGridDescription}>
                  {t('settings.dashboard.contentLocalization.description')}
                </p>
                <span style={styles.settingsGridArrow}>&#8594;</span>
              </div>
            </div>

            {/* Modules Overview Card - Only show if modules have been configured */}
            {checklistSteps.find(step => step.id === 'modules')?.completed && activeModules.length > 0 && (
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <h2 style={styles.cardTitle}>{t('settings.dashboard.modules.title')}</h2>
                  <div style={styles.modulesSummary}>
                    <span style={styles.activeCount}>{t('settings.dashboard.modules.active', { count: activeModules.length })}</span>
                  </div>
                </div>
                <div style={styles.cardBody}>
                  <div style={styles.compactModuleList}>
                    {activeModules.map((module) => (
                      <div key={module.id} style={styles.compactModuleCard}>
                        <span style={styles.moduleIcon}>{module.icon}</span>
                        <div style={styles.compactModuleInfo}>
                          <div style={styles.compactModuleName}>{module.name}</div>
                          <div style={styles.compactModuleDescription}>{module.description}</div>
                        </div>
                        <span style={{...styles.statusDot, ...styles.statusActive}}>●</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div style={styles.footerLeft}>
            <span>{config.texts?.general?.footerCopyright
              ? config.texts.general.footerCopyright.replace(/\b20\d{2}\b/, String(new Date().getFullYear()))
              : (config.branding?.footerText || `© ${new Date().getFullYear()} ${config.customer?.name || config.texts?.general?.applicationName || config.branding?.appName || 'Insurance'} - ${t('footer.allRightsReserved')}.`)}</span>
            {config.texts?.general?.footerPoweredBy && (
              <span style={{ marginLeft: '12px', opacity: 0.7 }}>{config.texts.general.footerPoweredBy}</span>
            )}
          </div>
          <div style={styles.footerRight}>
            <span style={styles.footerItem}>
              Instance: {config.customer?.id || 'boilerplate'}
            </span>
            <span style={styles.footerSeparator}>•</span>
            <span style={styles.footerItem}>
              Version: {config.version || '1.0'}
            </span>
            <span style={styles.footerSeparator}>•</span>
            <span style={styles.footerItem}>
              Last updated: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
      </footer>

      {/* Modals (kept for potential future use) */}
      <ModuleDetailsModal
        module={selectedModule}
        isOpen={showModuleModal}
        onClose={() => setShowModuleModal(false)}
        onToggle={handleToggleModule}
        allModules={modules}
      />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: '#F5F5F5',
  },

  // Header
  header: {
    backgroundColor: 'var(--primary-color, #056666)',
    color: 'white',
    padding: '20px 40px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  headerContent: {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  logo: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    objectFit: 'contain' as const,
    backgroundColor: 'white',
    padding: '4px',
  },
  logoPlaceholder: {
    width: '48px',
    height: '48px',
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    fontWeight: '700' as const,
  },
  headerTitle: {
    margin: 0,
    fontSize: '24px',
    fontWeight: '600' as const,
  },
  headerSubtitle: {
    margin: '4px 0 0 0',
    fontSize: '14px',
    opacity: 0.9,
  },
  headerRight: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  environmentBadge: {
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '700' as const,
    color: 'white',
    letterSpacing: '0.5px',
  },
  instanceBadge: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '14px',
  },
  instanceLabel: {
    opacity: 0.8,
    marginRight: '8px',
  },
  instanceId: {
    fontWeight: '600' as const,
    textTransform: 'uppercase' as const,
  },

  // Main Dashboard
  main: {
    flex: 1,
    padding: '32px 40px',
  },
  notificationBanner: {
    maxWidth: '1400px',
    margin: '0 auto 24px auto',
    backgroundColor: '#fef3c7',
    border: '2px solid #f59e0b',
    borderRadius: '8px',
    padding: '16px 20px',
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    color: '#92400e',
  },
  notificationIcon: {
    fontSize: '20px',
    flexShrink: 0,
  },
  dashboard: {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: '350px 1fr',
    gap: '24px',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '24px',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '24px',
  },

  // Cards
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
  featuredCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    overflow: 'hidden',
    border: '2px solid var(--primary-color, #056666)',
  },
  sectionLabel: {
    fontSize: '11px',
    fontWeight: '700' as const,
    color: 'var(--primary-color, #056666)',
    letterSpacing: '1px',
    marginBottom: '4px',
  },
  cardHeader: {
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600' as const,
    color: '#1f2937',
  },
  cardBody: {
    padding: '24px',
  },
  editButton: {
    padding: '6px 12px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },

  // System Information
  systemInfoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: '1px solid #f3f4f6',
    alignItems: 'center',
  },
  systemInfoLabel: {
    fontSize: '13px',
    color: '#6b7280',
    fontWeight: '500' as const,
  },
  systemInfoValue: {
    fontSize: '13px',
    color: '#1f2937',
    fontWeight: '600' as const,
  },
  environmentBadgeSmall: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: '700' as const,
    color: 'white',
    letterSpacing: '0.5px',
  },

  // Customer Info & Status
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 0',
    borderBottom: '1px solid #f3f4f6',
    alignItems: 'center',
  },
  infoLabel: {
    fontSize: '14px',
    color: '#6b7280',
    fontWeight: '500' as const,
  },
  infoValue: {
    fontSize: '14px',
    color: '#1f2937',
    fontWeight: '600' as const,
  },
  link: {
    fontSize: '14px',
    color: 'var(--primary-color, #056666)',
    textDecoration: 'none',
    fontWeight: '600' as const,
  },
  notSet: {
    color: '#9ca3af',
    fontStyle: 'italic' as const,
  },
  statusConfigured: {
    fontSize: '13px',
    fontWeight: '600' as const,
    color: '#10b981',
    padding: '4px 8px',
    backgroundColor: '#d1fae5',
    borderRadius: '4px',
  },
  statusNotConfigured: {
    fontSize: '13px',
    fontWeight: '600' as const,
    color: '#f59e0b',
    padding: '4px 8px',
    backgroundColor: '#fef3c7',
    borderRadius: '4px',
  },
  customerLogoSection: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
    paddingBottom: '20px',
    borderBottom: '1px solid #f3f4f6',
  },
  customerLogo: {
    width: '80px',
    height: '80px',
    objectFit: 'contain' as const,
    borderRadius: '8px',
    border: '2px solid #e5e7eb',
    padding: '8px',
  },
  customerLogoPlaceholder: {
    width: '80px',
    height: '80px',
    backgroundColor: '#f3f4f6',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    fontWeight: '700' as const,
    color: '#9ca3af',
    border: '2px dashed #d1d5db',
  },
  colorSwatchContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  colorSwatch: {
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    border: '2px solid #e5e7eb',
  },
  colorCode: {
    fontSize: '13px',
    fontFamily: 'monospace',
    color: '#6b7280',
  },

  // Contact Section
  contactSection: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #f3f4f6',
  },
  contactSectionTitle: {
    margin: '0 0 12px 0',
    fontSize: '13px',
    fontWeight: '700' as const,
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  contactRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '10px 0',
    borderBottom: '1px solid #F5F5F5',
  },
  contactIcon: {
    fontSize: '18px',
    flexShrink: 0,
    marginTop: '2px',
  },
  contactInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  contactLabel: {
    fontSize: '11px',
    color: '#9ca3af',
    fontWeight: '600' as const,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  contactLink: {
    fontSize: '14px',
    color: 'var(--primary-color, #056666)',
    textDecoration: 'none',
    fontWeight: '500' as const,
    wordBreak: 'break-all' as const,
  },
  emptyContactState: {
    padding: '16px 0',
    textAlign: 'center' as const,
  },
  customerInfoSection: {
    marginBottom: '16px',
  },
  customerNameBlock: {
    textAlign: 'center' as const,
    padding: '12px 0',
    borderBottom: '1px solid #f3f4f6',
  },
  customerName: {
    margin: '0 0 8px 0',
    fontSize: '20px',
    fontWeight: '700' as const,
    color: '#1f2937',
  },
  customerTagline: {
    margin: 0,
    fontSize: '14px',
    fontWeight: '500' as const,
    color: '#6b7280',
    fontStyle: 'italic' as const,
  },

  // Branding Preview Section
  brandingPreview: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #f3f4f6',
  },
  brandingColorGrid: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap' as const,
    marginBottom: '12px',
  },
  brandingColorItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '6px',
  },
  brandingColorSwatch: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    border: '2px solid #e5e7eb',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  brandingColorLabel: {
    fontSize: '11px',
    color: '#9ca3af',
    fontWeight: '600' as const,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  fontPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: '#F5F5F5',
    borderRadius: '6px',
    marginTop: '8px',
  },
  fontPreviewLabel: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: '600' as const,
  },
  fontPreviewValue: {
    fontSize: '16px',
    fontWeight: '500' as const,
    color: '#1f2937',
  },

  // Settings Navigation Grid
  settingsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  settingsGridCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    textAlign: 'center' as const,
    padding: '20px 16px',
    backgroundColor: 'white',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    position: 'relative' as const,
    minHeight: '160px',
  },
  settingsGridIcon: {
    width: '48px',
    height: '48px',
    backgroundColor: 'var(--primary-color, #056666)',
    color: 'white',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    fontWeight: '700' as const,
    marginBottom: '12px',
  },
  settingsGridTitle: {
    margin: '0 0 8px 0',
    fontSize: '15px',
    fontWeight: '600' as const,
    color: '#1f2937',
  },
  settingsGridDescription: {
    margin: 0,
    fontSize: '12px',
    color: '#6b7280',
    lineHeight: '1.4',
    flex: 1,
  },
  settingsGridWarning: {
    display: 'block',
    marginTop: '8px',
    color: '#f59e0b',
    fontWeight: 600,
    fontSize: '11px',
  },
  settingsGridArrow: {
    position: 'absolute' as const,
    top: '12px',
    right: '12px',
    fontSize: '16px',
    color: '#9ca3af',
    fontWeight: '700' as const,
  },

  // Content & Localization Navigation Card
  contentNavigationCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '24px',
    backgroundColor: 'white',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  contentNavLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  contentNavIcon: {
    width: '56px',
    height: '56px',
    backgroundColor: 'var(--primary-color, #056666)',
    color: 'white',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    fontWeight: '700' as const,
    flexShrink: 0,
  },
  contentNavInfo: {
    flex: 1,
  },
  contentNavTitle: {
    margin: '4px 0 8px 0',
    fontSize: '18px',
    fontWeight: '600' as const,
    color: '#1f2937',
  },
  contentNavDescription: {
    margin: 0,
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: '1.4',
  },
  contentNavArrow: {
    fontSize: '24px',
    color: '#9ca3af',
    fontWeight: '700' as const,
    flexShrink: 0,
  },

  // Quick Links
  quickLinkButton: {
    width: '100%',
    padding: '14px 16px',
    marginBottom: '8px',
    backgroundColor: '#F5F5F5',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500' as const,
    color: '#1f2937',
    transition: 'all 0.2s',
  },
  quickLinkButtonDanger: {
    width: '100%',
    padding: '14px 16px',
    marginBottom: '8px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600' as const,
    color: '#dc2626',
    transition: 'all 0.2s',
  },
  quickLinkDivider: {
    height: '1px',
    backgroundColor: '#e5e7eb',
    margin: '12px 0',
  },
  quickLinkIcon: {
    fontSize: '16px',
  },

  // Modules
  modulesSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  },
  activeCount: {
    color: '#10b981',
    fontWeight: '600' as const,
  },
  inactiveCount: {
    color: '#6b7280',
    fontWeight: '600' as const,
  },
  separator: {
    color: '#d1d5db',
  },
  moduleSection: {
    marginBottom: '24px',
  },
  moduleSectionHeader: {
    backgroundColor: '#d1fae5',
    padding: '8px 12px',
    borderRadius: '6px',
    marginBottom: '12px',
  },
  moduleSectionTitle: {
    margin: 0,
    fontSize: '13px',
    fontWeight: '700' as const,
    color: '#10b981',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  moduleSectionHeaderInactive: {
    backgroundColor: '#f3f4f6',
    padding: '8px 12px',
    borderRadius: '6px',
    marginBottom: '12px',
  },
  moduleSectionTitleInactive: {
    margin: 0,
    fontSize: '13px',
    fontWeight: '700' as const,
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  compactModuleList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  compactModuleCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#F5F5F5',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
  },
  compactModuleCardInactive: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#fafafa',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    opacity: 0.7,
  },
  compactModuleInfo: {
    flex: 1,
  },
  compactModuleName: {
    fontSize: '14px',
    fontWeight: '600' as const,
    color: '#1f2937',
    marginBottom: '2px',
  },
  compactModuleNameInactive: {
    fontSize: '14px',
    fontWeight: '600' as const,
    color: '#6b7280',
    marginBottom: '2px',
  },
  compactModuleDescription: {
    fontSize: '12px',
    color: '#6b7280',
    lineHeight: '1.4',
  },
  moduleIconInactive: {
    fontSize: '24px',
    opacity: 0.5,
  },
  moduleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '16px',
  },
  moduleCard: {
    padding: '16px',
    backgroundColor: '#F5F5F5',
    borderRadius: '8px',
    border: '2px solid #e5e7eb',
    cursor: 'pointer',
    transition: 'all 0.2s',
    position: 'relative' as const,
  },
  moduleCardHover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    borderColor: 'var(--primary-color, #056666)',
  },
  moduleCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  moduleIcon: {
    fontSize: '24px',
  },
  statusDot: {
    fontSize: '16px',
  },
  statusActive: {
    color: '#10b981',
  },
  statusInactive: {
    color: '#9ca3af',
  },
  moduleCardTitle: {
    margin: '0 0 4px 0',
    fontSize: '14px',
    fontWeight: '600' as const,
    color: '#1f2937',
  },
  moduleCardDescription: {
    margin: '0 0 12px 0',
    fontSize: '12px',
    color: '#6b7280',
    lineHeight: '1.4',
  },
  moduleCardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  moduleBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    backgroundColor: '#e0e7ff',
    color: '#4f46e5',
    fontSize: '11px',
    fontWeight: '600' as const,
    borderRadius: '4px',
  },
  moduleAction: {
    fontSize: '12px',
    color: 'var(--primary-color, #056666)',
    fontWeight: '600' as const,
  },
  moduleCardInactive: {
    opacity: 0.7,
    backgroundColor: '#fafafa',
  },

  // Category Sections
  categorySection: {
    marginBottom: '32px',
  },
  categoryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: '2px solid #e5e7eb',
  },
  categoryTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: '700' as const,
    color: '#1f2937',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  categoryCount: {
    fontSize: '13px',
    color: '#6b7280',
    fontWeight: '600' as const,
  },

  // Dependency Badge
  dependencyBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    backgroundColor: '#f3f4f6',
    borderRadius: '6px',
    marginBottom: '8px',
  },
  warningIcon: {
    fontSize: '14px',
  },
  successIcon: {
    fontSize: '14px',
  },
  dependencyText: {
    fontSize: '11px',
    color: '#6b7280',
    fontWeight: '600' as const,
  },

  // Empty States
  emptyStateCard: {
    padding: '60px 20px',
    textAlign: 'center' as const,
    backgroundColor: '#F5F5F5',
    borderRadius: '12px',
    border: '2px dashed #d1d5db',
  },
  friendlyEmptyState: {
    padding: '40px 20px',
    textAlign: 'center' as const,
    backgroundColor: '#eff6ff',
    borderRadius: '12px',
    marginBottom: '24px',
  },
  emptyStateIcon: {
    fontSize: '64px',
    display: 'block',
    marginBottom: '16px',
  },
  emptyStateTitle: {
    margin: '0 0 8px 0',
    fontSize: '18px',
    fontWeight: '600' as const,
    color: '#1f2937',
  },
  emptyStateDescription: {
    margin: '0 0 24px 0',
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: '1.6',
  },
  emptyStateButton: {
    padding: '10px 20px',
    backgroundColor: 'var(--primary-color, #056666)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },

  moduleList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  moduleListItem: {
    padding: '12px',
    backgroundColor: '#F5F5F5',
    borderRadius: '6px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  moduleListLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  moduleListIcon: {
    fontSize: '20px',
  },
  moduleListName: {
    fontSize: '14px',
    fontWeight: '600' as const,
    color: '#1f2937',
  },
  moduleListDescription: {
    fontSize: '12px',
    color: '#6b7280',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '40px 20px',
    color: '#9ca3af',
  },
  emptyStateHint: {
    fontSize: '14px',
    marginTop: '8px',
  },

  // Progress
  setupSummary: {
    fontSize: '13px',
    fontWeight: '600' as const,
    color: '#6b7280',
  },
  progressContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
  },
  progressBar: {
    width: '120px',
    height: '8px',
    backgroundColor: '#e5e7eb',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  progressBarLarge: {
    flex: 1,
    height: '12px',
    backgroundColor: '#e5e7eb',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#10b981',
    transition: 'width 0.3s',
  },
  progressText: {
    fontSize: '14px',
    fontWeight: '600' as const,
    color: '#6b7280',
  },
  progressTextLarge: {
    fontSize: '16px',
    fontWeight: '700' as const,
    color: '#10b981',
    minWidth: '50px',
  },

  // Checklist
  checklistItem: {
    display: 'flex',
    gap: '12px',
    padding: '16px 0',
    borderBottom: '1px solid #f3f4f6',
    alignItems: 'flex-start',
  },
  checklistAction: {
    fontSize: '13px',
    color: 'var(--primary-color, #056666)',
    textDecoration: 'none',
    fontWeight: '600' as const,
    padding: '4px 8px',
    borderRadius: '4px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    transition: 'background 0.2s',
    whiteSpace: 'nowrap' as const,
  },
  checkbox: {
    width: '24px',
    height: '24px',
    border: '2px solid #d1d5db',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: '14px',
    color: 'white',
  },
  checkboxChecked: {
    width: '24px',
    height: '24px',
    border: '2px solid #10b981',
    backgroundColor: '#10b981',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: '14px',
    color: 'white',
    fontWeight: '700' as const,
  },
  checklistTitle: {
    fontSize: '14px',
    fontWeight: '600' as const,
    color: '#1f2937',
    marginBottom: '4px',
  },
  checklistDescription: {
    fontSize: '13px',
    color: '#6b7280',
    lineHeight: '1.4',
  },
  statusChecklistContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  statusChecklistItem: {
    display: 'flex',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#F5F5F5',
    borderRadius: '6px',
    alignItems: 'center',
  },

  // Setup Steps List (Clickable Navigation Design)
  setupStepsList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  setupStepRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    backgroundColor: '#F5F5F5',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  setupStepRowCompleted: {
    backgroundColor: '#d1fae5',
    borderColor: '#10b981',
  },
  setupStepRowDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  setupStepRowLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flex: 1,
  },
  setupStepRowIcon: {
    fontSize: '28px',
  },
  setupStepRowInfo: {
    flex: 1,
  },
  setupStepRowTitle: {
    margin: '0 0 4px 0',
    fontSize: '15px',
    fontWeight: '600' as const,
    color: '#1f2937',
  },
  setupStepRowDescription: {
    margin: '0',
    fontSize: '13px',
    color: '#6b7280',
    lineHeight: '1.4',
  },
  setupStepRowDisabledReason: {
    margin: '6px 0 0 0',
    fontSize: '12px',
    color: '#f59e0b',
    fontWeight: '600' as const,
  },
  setupStepRowRight: {
    display: 'flex',
    alignItems: 'center',
  },
  setupStepRowArrow: {
    fontSize: '20px',
    color: '#6b7280',
    fontWeight: '700' as const,
  },
  statusIconCompleted: {
    width: '32px',
    height: '32px',
    backgroundColor: '#10b981',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: '700' as const,
    flexShrink: 0,
  },
  statusIconPending: {
    width: '32px',
    height: '32px',
    border: '2px solid #d1d5db',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    color: '#9ca3af',
    flexShrink: 0,
  },

  // Start Workflow Button
  startWorkflowContainer: {
    marginTop: '24px',
    textAlign: 'center' as const,
  },
  startWorkflowButton: {
    width: '100%',
    padding: '16px 24px',
    backgroundColor: 'var(--primary-color, #056666)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '700' as const,
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  startWorkflowButtonDisabled: {
    backgroundColor: '#d1d5db',
    color: '#9ca3af',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  startWorkflowIcon: {
    fontSize: '20px',
  },
  startWorkflowHint: {
    margin: '12px 0 0 0',
    fontSize: '13px',
    color: '#6b7280',
    fontStyle: 'italic' as const,
  },
  generatingHint: {
    margin: '12px 0 0 0',
    fontSize: '13px',
    color: '#3b82f6',
    fontWeight: '600' as const,
    textAlign: 'center' as const,
  },
  spinner: {
    fontSize: '20px',
    animation: 'spin 1s linear infinite',
    display: 'inline-block',
  },
  buildProgressContainer: {
    marginTop: '24px',
    padding: '20px',
    backgroundColor: '#F5F5F5',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
  },
  buildProgressTitle: {
    margin: '0 0 16px 0',
    fontSize: '16px',
    fontWeight: '600' as const,
    color: '#1f2937',
  },
  buildProgressLog: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  buildProgressStep: {
    display: 'flex',
    gap: '12px',
    padding: '8px 12px',
    backgroundColor: 'white',
    borderRadius: '4px',
    border: '1px solid #e5e7eb',
    fontSize: '13px',
    fontFamily: 'monospace',
  },
  buildProgressStepNumber: {
    color: '#6b7280',
    fontWeight: '600' as const,
    minWidth: '24px',
  },
  buildProgressStepText: {
    color: '#1f2937',
    flex: 1,
  },
  errorMessage: {
    marginTop: '12px',
    padding: '12px 16px',
    backgroundColor: '#fee2e2',
    border: '2px solid #ef4444',
    borderRadius: '6px',
    color: '#991b1b',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  errorIcon: {
    fontSize: '16px',
    fontWeight: '700' as const,
  },

  // Footer
  footer: {
    backgroundColor: '#1f2937',
    color: 'white',
    padding: '20px 40px',
  },
  footerContent: {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '14px',
  },
  footerLeft: {
    opacity: 0.9,
  },
  footerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    opacity: 0.7,
  },
  footerItem: {
    fontSize: '13px',
  },
  footerSeparator: {
    opacity: 0.5,
  },

  // Loading state (for production redirect)
  loadingContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '20px',
  },
};

// Add responsive CSS via style tag
const responsiveStyles = `
  @media (max-width: 1024px) {
    .dashboard {
      grid-template-columns: 1fr !important;
    }
  }

  @media (max-width: 768px) {
    .header {
      padding: 16px 20px !important;
    }

    .headerContent {
      flex-direction: column !important;
      gap: 16px !important;
      align-items: flex-start !important;
    }

    .main {
      padding: 20px !important;
    }

    .moduleGrid {
      grid-template-columns: 1fr !important;
    }

    .footerContent {
      flex-direction: column !important;
      gap: 12px !important;
      text-align: center !important;
    }
  }
`;

// Inject responsive styles
if (typeof document !== 'undefined') {
  const styleElement = document.getElementById('homepage-responsive-styles');
  if (!styleElement) {
    const style = document.createElement('style');
    style.id = 'homepage-responsive-styles';
    style.textContent = responsiveStyles;
    document.head.appendChild(style);
  }
}

export default SettingsPage;
